import { evaluateCompanionTurn, type CompanionPolicy, type CompanionTurn } from './companion';
import type { Decision } from './domain';

const policy: CompanionPolicy = {
  principalId: 'human-owner',
  allowedInboundChannels: ['app', 'sms', 'imessage', 'email', 'web'],
  allowedProactiveChannels: ['app', 'imessage'],
  allowedPurposes: ['Daily check-in', 'Prepare approved action', 'Capture personal preference'],
  allowedDataScopes: ['private-profile', 'calendar-availability', 'personal-preferences'],
  allowTrustedContactMemoryWrites: false,
};

const now = new Date('2026-07-27T18:05:00.000Z');

const turns: CompanionTurn[] = [
  {
    id: 'companion-check-in',
    principalId: 'human-owner',
    profile: 'personal',
    channel: 'imessage',
    senderId: 'personal-twin',
    trust: 'principal',
    intent: 'REMIND',
    purpose: 'Daily check-in',
    dataScopes: [],
    sourceRefs: [],
    proactive: true,
    proactiveOptIn: true,
    quietHours: false,
    createdAt: '2026-07-27T18:00:00.000Z',
  },
  {
    id: 'companion-quiet-hours',
    principalId: 'human-owner',
    profile: 'personal',
    channel: 'imessage',
    senderId: 'personal-twin',
    trust: 'principal',
    intent: 'REMIND',
    purpose: 'Daily check-in',
    dataScopes: [],
    sourceRefs: [],
    proactive: true,
    proactiveOptIn: true,
    quietHours: true,
    createdAt: '2026-07-27T23:30:00.000Z',
  },
  {
    id: 'companion-external-memory',
    principalId: 'human-owner',
    profile: 'personal',
    channel: 'email',
    senderId: 'newsletter',
    trust: 'external',
    intent: 'WRITE_MEMORY',
    purpose: 'Capture personal preference',
    dataScopes: ['personal-preferences'],
    sourceRefs: ['source:newsletter:42'],
    proactive: false,
    proactiveOptIn: false,
    quietHours: false,
    createdAt: '2026-07-27T17:20:00.000Z',
  },
  {
    id: 'companion-execution-attempt',
    principalId: 'human-owner',
    profile: 'personal',
    channel: 'sms',
    senderId: 'human-owner',
    trust: 'principal',
    intent: 'EXECUTE_ACTION',
    purpose: 'Prepare approved action',
    dataScopes: ['calendar-availability'],
    sourceRefs: [],
    proactive: false,
    proactiveOptIn: false,
    quietHours: false,
    createdAt: '2026-07-27T17:45:00.000Z',
  },
];

const titleByTurn: Record<string, string> = {
  'companion-check-in': 'Consented daily check-in',
  'companion-quiet-hours': 'Proactive contact during quiet hours',
  'companion-external-memory': 'External content requests a memory write',
  'companion-execution-attempt': 'Message requests direct execution',
};

function DecisionBadge({ decision }: { decision: Decision }) {
  return <span className="decision" data-decision={decision}>{decision.replace('_', ' ')}</span>;
}

export function Companion() {
  const evaluations = turns.map((turn) => ({ turn, result: evaluateCompanionTurn(turn, policy, now) }));

  return <>
    <section className="surface-card page-card">
      <div className="section-header">
        <div>
          <p className="eyebrow">MESSAGE-FIRST PERSONAL AGENCY</p>
          <h2>One relationship. No ambient authority.</h2>
          <p>The companion can meet the human in app, SMS, iMessage, email and web. It may converse, remember with consent, follow up and prepare actions. Execution remains behind the governed boundary.</p>
        </div>
      </div>

      <div className="provider-strip">
        <span>App</span><span>SMS</span><span>iMessage</span><span>Email</span><span>Web</span><span>Future channel adapters</span>
      </div>

      <div className="decision-summary">
        {evaluations.map(({ turn, result }) => <div key={turn.id}>
          <div>
            <strong>{titleByTurn[turn.id]}</strong>
            <small>{turn.channel} · {turn.intent} · {result.reasons.join(' · ')}</small>
          </div>
          <DecisionBadge decision={result.decision} />
        </div>)}
      </div>

      <div className="principle-banner">
        <strong>Conversation is not authorization.</strong>
        <span>A message may create a proposal. Only principal binding, mandate, VAIG, REHT and RACS can clear consequential execution.</span>
      </div>
    </section>

    <section className="surface-card page-card">
      <div className="section-header">
        <div>
          <p className="eyebrow">CONTINUOUS BUT SUBORDINATE</p>
          <h2>Proactivity is a consented capability.</h2>
          <p>Every trigger is bound to profile, purpose, channel, data scope, timing and revocation. Quiet hours defer. Missing opt-in denies.</p>
        </div>
      </div>
      <div className="flow-line">
        <span>Channel</span><i>→</i><span>Identity</span><i>→</i><span>Consent</span><i>→</i><span>Memory boundary</span><i>→</i><span>Proposal</span><i>→</i><span>REHT</span><i>→</i><span>Receipt</span>
      </div>
      <div className="principle-banner">
        <strong>External content remains tainted.</strong>
        <span>Source references and taint markers survive conversion from a conversation into a governed action proposal.</span>
      </div>
    </section>
  </>;
}
